import { BusinessObjectBase } from './base';
import type { MessageListener } from './message-listener';
import type { MyUserTask } from './my-user-task';
import type { ProcessInstance } from './process-instance';
import type { ServiceTask } from './service-task';
import type { Timer } from './timer';
import type { TokenHistory } from './token-history';
import type { TokenLink } from './token-link';
import type { UserTask } from './user-task';

export interface TokenData {
  readonly id: string;
  readonly referenceNumber?: number;
  readonly activityId?: string;
  readonly status?: string;
  readonly errorMessage?: string;
  readonly processInstanceId: number;
  readonly processInstance?: ProcessInstance;
  readonly currentUserTaskId: number;
  readonly currentUserTask?: UserTask;
  readonly currentServiceTaskId: number;
  readonly currentServiceTask?: ServiceTask;
  readonly currentChildProcessInstanceId: number;
  readonly currentChildProcessInstance?: ProcessInstance;
  readonly userTasks?: UserTask[];
  readonly serviceTasks?: ServiceTask[];
  readonly childProcessInstances?: ProcessInstance[];
  readonly tokenHistories?: TokenHistory[];
  readonly downstreamLinks?: TokenLink[];
  readonly upstreamLinks?: TokenLink[];
  readonly timers?: Timer[];
  readonly messageListeners?: MessageListener[];
  readonly myUserTasks?: MyUserTask[];
}

export interface Token extends Readonly<TokenData> {}
export class Token extends BusinessObjectBase {
  static readonly entityName = 'Token' as const;
  static readonly plural = 'tokens';

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<Token[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new Token(data));
  }

  static async findOne(filter?: any): Promise<Token | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new Token(data) : null;
  }

  static async findById(id: string, filter?: any): Promise<Token> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new Token(data);
  }

  static async create(data: Omit<TokenData, 'id'>): Promise<Token> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new Token(result);
  }

  static async updateById(
    id: string,
    data: Partial<TokenData>,
  ): Promise<Token> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    const result = await this.patch<any>(url, data);
    return new Token(result);
  }

  static async deleteById(id: string): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  async updateState(options: any): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${Token.plural}/${(this as any).id}/update-state`;
    return BusinessObjectBase.post<any>(url, options);
  }
  async move(options: any): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${Token.plural}/${(this as any).id}/move`;
    return BusinessObjectBase.post<any>(url, options);
  }
  async fail(options: any): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${Token.plural}/${(this as any).id}/fail`;
    return BusinessObjectBase.post<any>(url, options);
  }
}
